import React from "react";
import { Formik, Field } from "formik";
import { Merchandise } from "../service-management-type";
import CancelIcon from "../../../../../assets/icons/cancel-icon.svg";
import { AntSwitch } from "../../../../../common/components/ant-switch/ant-switch";
import * as Yup from "yup";
import { ErrorMessage } from "formik";

interface Props {
  onClose: () => void;
  onSave: (data: Merchandise) => void;
  merchandise?: Merchandise;
  isReadOnly?: boolean;
}

const MerchandiseModal: React.FC<Props> = ({
  onClose,
  onSave,
  merchandise,
  isReadOnly,
}) => {
  const isEditing = !!merchandise;

  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Merchandise name is required")
      .min(3,`Merchandise name should be at least 3 characters`)
      .max(50, "Merchandise name can't exceed 50 characters"),
    code: Yup.string()
      .required("Merchandise code is required")
      .min(1, `Merchandise code should be at least 1 characters`)
      .max(50, "Merchandise code can't exceed 50 characters"),
    description: Yup.string()
      .min(3, "Description should be at least 3 characters")
      .max(300, "Description can't exceed 300 characters"),
    minPrice: Yup.number()
      .transform((value, originalValue) => {
        // Convert empty string to undefined to trigger required validation
        return originalValue === "" ? undefined : value;
      })
      .required("Min price is required")
      .min(0, "Min price cannot be negative")
      .max(999999, "Minimum price can't exceed 999999")
      .typeError("Min price must be a valid number")
      .test(
        "is-decimal",
        "Min price can have up to two decimal places",
        value => value === undefined || /^\d+(\.\d{1,2})?$/.test(value.toString())
      ),
    maxPrice: Yup.number()
      .transform((value, originalValue) => {
        // Convert empty string to undefined to trigger required validation
        return originalValue === "" ? undefined : value;
      })
      .required("Max price is required")
      .min(
        Yup.ref("minPrice"),
        "Max price should be greater than or equal to min price"
      )
      .max(999999, "Maximum price can't exceed 999999")
      .typeError("Max price must be a valid number")
      .test(
        "is-decimal",
        "Max price can have up to two decimal places",
        value => value === undefined || /^\d+(\.\d{1,2})?$/.test(value.toString())
      ),
  });

  const ErrorText = ({ name }: { name: string }) => (
    <ErrorMessage name={name} component="div" className="error-message" />
  );

  return (
    <div className="modal-backdrop">
      <Formik
        enableReinitialize
        initialValues={
          merchandise || {
            name: "",
            code: "",
            description: "",
            minPrice: "",
            maxPrice: "",
            status: true,
          }
        }
        validationSchema={validationSchema}
        validateOnBlur={true}
        validateOnChange={true}
        onSubmit={(values) => {
          // Convert string values to numbers before saving
          const sanitizedValues = {
            ...values,
            minPrice: typeof values.minPrice === 'string' ? parseFloat(values.minPrice) : values.minPrice,
            maxPrice: typeof values.maxPrice === 'string' ? parseFloat(values.maxPrice) : values.maxPrice,
          };
          onSave(sanitizedValues as Merchandise);
        }}
      >
        {({
          values,
          setFieldValue,
          touched,
          errors,
          submitCount,
          dirty,
          handleSubmit,
        }) => {
          return (
            <div
              className="modal-card sub-service-modal-card"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="header-wrapper">
                <div className="text-heading">
                  <h3 className="main-heading">
                    {isEditing ? "Edit Merchandise" : "Add Merchandise"}
                  </h3>
                  <span className="sub-text">
                    {isEditing
                      ? "Update merchandise details"
                      : "Create a new merchandise"}
                  </span>
                </div>
                <img src={CancelIcon} alt="Cancel" onClick={onClose} />
              </div>
              <div className="body-content-wrapper">
                <label>
                  Merchandise Name <span className="required">*</span>
                </label>
                <Field
                  name="name"
                  placeholder="Enter merchandise name"
                  maxLength={50}
                  disabled={isReadOnly}
                />
                <ErrorText name="name" />
                <label>
                  {" "}
                  Merchandise Code <span className="required">*</span>
                </label>
                <Field
                  name="code"
                  placeholder="Enter merchandise code"
                  maxLength={50}
                  disabled={isReadOnly}
                />
                <ErrorText name="code" />

                <label>Merchandise Description</label>
                <Field
                  name="description"
                  as="textarea"
                  placeholder="Enter detailed description"
                  maxLength={300}
                  disabled={isReadOnly}
                />
                <ErrorText name="description" />
                <label>
                  Base Price ($)
                  <span className="required">*</span>
                </label>
                <div className="row">
                  <div className="price-field">
                    <Field
                      name="minPrice"
                      type="number"
                      placeholder="$ Min Value"
                      disabled={isReadOnly}
                       onWheel={(e) =>
                        e.target instanceof HTMLElement && e.target.blur()
                      }
                    />
                    <ErrorText name="minPrice" />
                  </div>
                  <div className="price-field">
                    <Field
                      name="maxPrice"
                      type="number"
                      placeholder="$ Max Value"
                      disabled={isReadOnly}
                       onWheel={(e) =>
                        e.target instanceof HTMLElement && e.target.blur()
                      }
                    />
                    <ErrorText name="maxPrice" />
                  </div>
                </div>

                <div className="status-wrapper">
                  <div className="toggle-row">
                    <span className="status-label">Status</span>
                    <AntSwitch
                      checked={values.status === true || (values.status === "active" ? true : false)}
                      onChange={(e, checked) =>
                        !isReadOnly && setFieldValue("status", checked)
                      }
                      disabled={!!isReadOnly}
                    />
                  </div>
                  <span className="notes">
                    Set merchandise as active or inactive
                  </span>
                </div>

                <div className="modal-actions buttons-wrapper">
                  <button
                    type="button"
                    onClick={onClose}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="primary submit-button"
                    disabled={!dirty || !!isReadOnly}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!isReadOnly) handleSubmit();
                    }}
                  >
                    {isEditing ? "Update Merchandise" : "Add Merchandise"}
                  </button>
                </div>
              </div>
            </div>
          );
        }}
      </Formik>
    </div>
  );
};

export default MerchandiseModal;
